import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Pencil } from 'lucide-react';
import {
  ErrorState,
  FilterChip,
  PersonCell,
  SearchField,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
} from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { MISSING, formatCount, formatDate, formatNumber, formatTime } from '../../../utils/format';
import CheckpointMark from './CheckpointMark';
import {
  asSentence,
  hasMissedAny,
  hasMissedEnd,
  hasMissedStart,
  repNeedsAttention,
  repStatus,
  tidyRange,
} from './helpers';
import type { RosterFilter, WeekendShiftRosterData, WeekendShiftRosterRow } from './types';

type FilterOption = {
  value: RosterFilter;
  label: string;
  match: (rep: WeekendShiftRosterRow, overCapIds: Set<string>) => boolean;
};

const FILTERS: FilterOption[] = [
  { value: 'all', label: 'All reps', match: () => true },
  { value: 'missed_any', label: 'Missed a check-in', match: hasMissedAny },
  { value: 'missed_start', label: 'Missed start', match: hasMissedStart },
  { value: 'missed_end', label: 'Missed end', match: hasMissedEnd },
  { value: 'no_shifts', label: 'No shifts', match: (rep) => rep.shiftCount === 0 },
  { value: 'two_plus', label: '2+ shifts', match: (rep) => rep.shiftCount >= 2 },
  { value: 'over_cap', label: 'Over cap', match: (rep, overCapIds) => overCapIds.has(rep.employeeId) },
  { value: 'suspended', label: 'Suspended', match: (rep) => Boolean(rep.suspensionStatus?.isSuspended) },
];

const COLUMNS = 7;
const PAGE_SIZES = [50, 100, 200];

const onShift = (rep: WeekendShiftRosterRow, key: string) => rep.selectedShiftKeys.includes(key);

const formatSubmitted = (value: string | null) =>
  value ? `${formatDate(value, { short: true })}, ${formatTime(value)}` : MISSING;

export interface RosterTabProps {
  rosterData: WeekendShiftRosterData | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  weekendLabel: string;
  /** Roster already narrowed by `filter` (the page owns that rule). */
  filteredRoster: WeekendShiftRosterRow[];
  filter: RosterFilter;
  onFilterChange: (filter: RosterFilter) => void;
  /** Shift key to narrow to, or null for every shift. */
  shiftFilter: string | null;
  onShiftFilterChange: (shiftKey: string | null) => void;
  onOpenRep: (rep: WeekendShiftRosterRow) => void;
}

/**
 * The selected weekend's roster: every sales rep, the shifts they hold, and
 * the start and end check-in state of each shift. Reps who need a look come
 * first, then reps on shifts in shift order, then reps with no shifts.
 */
const RosterTab: React.FC<RosterTabProps> = ({
  rosterData,
  loading,
  error,
  onRetry,
  weekendLabel,
  filteredRoster,
  filter,
  onFilterChange,
  shiftFilter,
  onShiftFilterChange,
  onOpenRep,
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  // The sales team is about 120 reps, so one page usually holds everyone.
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const roster = useMemo(() => rosterData?.roster ?? [], [rosterData]);
  const shiftOptions = useMemo(() => rosterData?.shiftOptions ?? [], [rosterData]);
  const overCapIds = useMemo(() => new Set(rosterData?.overCapRepIds ?? []), [rosterData]);
  const shortLabelByKey = useMemo(
    () => new Map(shiftOptions.map((option) => [option.key, option.shortLabel])),
    [shiftOptions]
  );
  const shiftOrder = useMemo(() => new Map(shiftOptions.map((option, index) => [option.key, index])), [shiftOptions]);

  // A shift picked on another weekend (a holiday shift) may not exist here.
  const activeShift = shiftFilter && shortLabelByKey.has(shiftFilter) ? shiftFilter : null;

  // Each filter row counts within the other row's choice, so a count is
  // always the number of rows that chip would show.
  const filterCounts = useMemo(() => {
    const scoped = activeShift ? roster.filter((rep) => onShift(rep, activeShift)) : roster;
    return Object.fromEntries(
      FILTERS.map((option) => [option.value, scoped.filter((rep) => option.match(rep, overCapIds)).length])
    ) as Record<RosterFilter, number>;
  }, [roster, activeShift, overCapIds]);
  const shiftCounts = useMemo(
    () => new Map(shiftOptions.map((option) => [option.key, filteredRoster.filter((rep) => onShift(rep, option.key)).length])),
    [shiftOptions, filteredRoster]
  );

  const visibleRoster = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = filteredRoster.filter((rep) => {
      if (activeShift && !onShift(rep, activeShift)) return false;
      if (!query) return true;
      const haystack = [
        rep.name,
        rep.email,
        rep.teamName ?? 'Sales',
        ...(rep.selectedShiftLabels ?? []),
        ...(rep.selectedShiftLabels ?? []).map(tidyRange),
        ...rep.selectedShiftKeys.map((key) => tidyRange(shortLabelByKey.get(key) ?? '')),
      ];
      return haystack.some((value) => value?.toLowerCase().includes(query));
    });

    const group = (rep: WeekendShiftRosterRow) =>
      repNeedsAttention(rep, overCapIds.has(rep.employeeId)) ? 0 : rep.shiftCount > 0 ? 1 : 2;
    const firstShift = (rep: WeekendShiftRosterRow) =>
      Math.min(...rep.selectedShiftKeys.map((key) => shiftOrder.get(key) ?? Number.MAX_SAFE_INTEGER), Number.MAX_SAFE_INTEGER);

    return rows
      .map((rep) => ({ rep, group: group(rep), first: firstShift(rep) }))
      .sort((a, b) =>
        a.group - b.group
        || (a.group === 1 ? a.first - b.first : 0)
        || a.rep.name.localeCompare(b.rep.name)
      )
      .map(({ rep }) => rep);
  }, [filteredRoster, activeShift, search, shortLabelByKey, overCapIds, shiftOrder]);

  useEffect(() => {
    setPage(0);
  }, [filter, activeShift, search, rosterData?.weekendStartDate]);

  const pageRows = visibleRoster.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const activeFilterLabel = FILTERS.find((option) => option.value === filter)?.label ?? 'All reps';
  const activeShiftLabel = activeShift ? tidyRange(shortLabelByKey.get(activeShift) ?? activeShift) : null;
  const scopeLabel = activeShiftLabel ? `${activeFilterLabel} on ${activeShiftLabel}` : activeFilterLabel;
  const clearFilters = () => {
    onFilterChange('all');
    onShiftFilterChange(null);
    setSearch('');
  };
  const visibleFilters = FILTERS.filter(
    (option) => option.value !== 'over_cap' || filter === 'over_cap' || (rosterData?.overCapRepIds?.length ?? 0) > 0
  );

  const chipRowLabel = (text: string) => (
    <Typography variant="labelSm" component="span" aria-hidden="true" sx={{ color: sbg.text.secondary, minWidth: 48 }}>
      {text}
    </Typography>
  );

  const toolbar = (
    <Box sx={{ display: 'grid', gap: 1.5, width: '100%', py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <SearchField
          value={search}
          onChange={setSearch}
          label="Search reps"
          placeholder="Search name, email, team or shift"
          disabled={!rosterData}
        />
        <Typography variant="body2" sx={{ color: sbg.text.secondary, ml: { sm: 'auto' } }} aria-live="polite">
          {rosterData
            ? `${formatCount(visibleRoster.length, 'rep')} shown · ${weekendLabel}`
            : weekendLabel}
        </Typography>
      </Box>
      <Box role="group" aria-label="Filter reps by status" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {chipRowLabel('Status')}
        {visibleFilters.map((option) => (
          <FilterChip
            key={option.value}
            selected={filter === option.value}
            onClick={() => onFilterChange(option.value)}
            count={rosterData ? filterCounts[option.value] : undefined}
          >
            {option.label}
          </FilterChip>
        ))}
      </Box>
      {shiftOptions.length > 0 && (
        <Box role="group" aria-label="Filter reps by shift" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {chipRowLabel('Shift')}
          <FilterChip selected={!activeShift} onClick={() => onShiftFilterChange(null)}>
            Any shift
          </FilterChip>
          {shiftOptions.map((option) => (
            <FilterChip
              key={option.key}
              selected={activeShift === option.key}
              onClick={() => onShiftFilterChange(activeShift === option.key ? null : option.key)}
              count={shiftCounts.get(option.key)}
            >
              {tidyRange(option.shortLabel)}
            </FilterChip>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderShifts = (rep: WeekendShiftRosterRow) => {
    if (rep.selectedShiftDetails?.length) {
      return (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.75 }}>
          {rep.selectedShiftDetails.map((detail) => {
            const notOpenYet =
              detail.startCheckin.checkinStatus === 'scheduled' && detail.endCheckin.checkinStatus === 'scheduled';
            return (
              <Box component="li" key={detail.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 22 }}>
                <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, minWidth: 64 }}>
                  {tidyRange(shortLabelByKey.get(detail.key) ?? detail.label)}
                </Typography>
                {notOpenYet ? (
                  <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                    Check-ins not open yet
                  </Typography>
                ) : (
                  <>
                    <CheckpointMark phase="start" checkpoint={detail.startCheckin} />
                    <CheckpointMark phase="end" checkpoint={detail.endCheckin} />
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      );
    }
    if (rep.selectedShiftLabels?.length) {
      return (
        <Typography variant="body2" sx={{ color: sbg.text.primary, pt: 1 }}>
          {rep.selectedShiftLabels.map(tidyRange).join(', ')}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" component="span" sx={{ color: sbg.text.tertiary, display: 'block', pt: 1 }}>
        {MISSING}
      </Typography>
    );
  };

  const renderBody = () => {
    if (!rosterData && loading) return <TableSkeletonRows columns={COLUMNS} rows={8} />;
    if (!rosterData && error) {
      return (
        <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          <TableCell colSpan={COLUMNS} sx={{ borderBottom: 0 }}>
            <ErrorState
              compact
              title="Couldn’t load the weekend roster"
              body={asSentence(error)}
              onRetry={onRetry}
            />
          </TableCell>
        </TableRow>
      );
    }
    if (roster.length === 0) {
      return (
        <TableEmptyRow
          colSpan={COLUMNS}
          title="No sales reps on this roster"
          body="Active sales reps appear here for every weekend, with the shifts they signed up for."
        />
      );
    }
    if (visibleRoster.length === 0) {
      return (
        <TableEmptyRow
          colSpan={COLUMNS}
          title="No reps match these filters"
          body={
            search.trim()
              ? `Nobody in “${scopeLabel}” matches “${search.trim()}”.`
              : `No reps fall under “${scopeLabel}” for ${weekendLabel}.`
          }
          action={
            <Button variant="outlined" size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      );
    }

    return pageRows.map((rep) => {
      const status = repStatus(rep, overCapIds.has(rep.employeeId));
      return (
        <TableRow
          key={rep.employeeId}
          hover
          onClick={() => onOpenRep(rep)}
          sx={{ cursor: 'pointer', verticalAlign: 'top' }}
        >
          <TableCell sx={{ minWidth: 220, maxWidth: 280 }}>
            <PersonCell name={rep.name} secondary={rep.email} />
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap', pt: 2 }}>{rep.teamName || 'Sales'}</TableCell>
          <TableCell align="right" sx={{ pt: 2 }}>{formatNumber(rep.shiftCount)}</TableCell>
          <TableCell sx={{ minWidth: 340, pt: 1.75 }}>{renderShifts(rep)}</TableCell>
          <TableCell sx={{ pt: 1.75 }}>
            {status.pills.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {status.pills.map((pill) => (
                  <StatusPill key={pill.label} tone={pill.tone}>{pill.label}</StatusPill>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, display: 'block', pt: '2px', whiteSpace: 'nowrap' }}>
                {status.summary}
              </Typography>
            )}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap', color: sbg.text.secondary, pt: 2 }}>
            {formatSubmitted(rep.submittedAt)}
          </TableCell>
          <TableCell align="right" sx={{ pt: 1.25, width: 56 }}>
            <Tooltip title="Manage shifts">
              <IconButton
                size="small"
                aria-label={`Manage shifts for ${rep.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenRep(rep);
                }}
              >
                <Pencil size={16} />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <TableCard
      aria-label={`Weekend roster, ${weekendLabel}`}
      toolbar={toolbar}
      footer={
        rosterData && visibleRoster.length > PAGE_SIZES[0] ? (
          <TablePagination
            component="div"
            count={visibleRoster.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZES}
            labelRowsPerPage="Reps per page"
          />
        ) : undefined
      }
    >
      <Table sx={{ minWidth: 1060 }} aria-busy={loading || undefined}>
        <TableHead>
          <TableRow>
            <TableCell>Rep</TableCell>
            <TableCell>Team</TableCell>
            <TableCell align="right">Shifts</TableCell>
            <TableCell>Shifts and check-ins</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Submitted</TableCell>
            <TableCell align="right">
              <span className="sbg-sr-only">Actions</span>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderBody()}</TableBody>
      </Table>
    </TableCard>
  );
};

export default RosterTab;
